import React, { useEffect, useState } from "react";
import {
  ChatCircleDots,
  FacebookLogo,
  Globe,
  InstagramLogo,
  LinkedinLogo,
  TiktokLogo,
  TwitterLogo,
  WhatsappLogo,
  YoutubeLogo,
} from "@phosphor-icons/react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getSocialLinks } from "../redux/actions/action-creator";

const upload = (path) => `https://rghtnow.com/wp-content/uploads/${path}`;

const footerLinks = [
  { to: "/about-page", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/vendors-delivery-partners", label: "Vendors & Delivery Partners" },
  { to: "/advertise-with-us", label: "Advertise With Us" },
  { to: "/contact-us", label: "Contact Us" },
];

const socialMeta = [
  { key: "chat", label: "Chat With Us", icon: ChatCircleDots, keywords: ["chatwithus", "chat", "tawk"] },
  { key: "facebook", label: "Facebook", icon: FacebookLogo, keywords: ["facebook", "fb"] },
  { key: "instagram", label: "Instagram", icon: InstagramLogo, keywords: ["instagram", "insta"] },
  { key: "twitter", label: "Twitter", icon: TwitterLogo, keywords: ["twitter", "xcom", "tco"] },
  { key: "linkedin", label: "LinkedIn", icon: LinkedinLogo, keywords: ["linkedin"] },
  { key: "youtube", label: "YouTube", icon: YoutubeLogo, keywords: ["youtube", "youtu"] },
  { key: "whatsapp", label: "WhatsApp", icon: WhatsappLogo, keywords: ["whatsapp", "wa"] },
  { key: "tiktok", label: "TikTok", icon: TiktokLogo, keywords: ["tiktok"] },
  { key: "web", label: "Web", icon: Globe, keywords: ["web", "website", "shoprghtnow", "rghtnow"] },
];

const getSocialLinksArray = (payload) => {
  const candidates = [
    payload?.data?.social_links,
    payload?.data?.data?.social_links,
    payload?.social_links,
    payload?.data,
    payload,
  ];

  return candidates.find((item) => Array.isArray(item)) || [];
};

const normalizeText = (value) => String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

const normalizeSocialUrl = (value) => {
  const source = String(value || "").trim();
  const markdownLink = source.match(/\[[^\]]*]\(<?([^)>]+)>?\)/);
  const url = (markdownLink ? markdownLink[1] : source)
    .replace(/\\([&?=])/g, "$1")
    .replace(/&amp;/g, "&")
    .trim();

  if (!url || url === "#") return "";
  if (/^(https?:|mailto:|tel:)/i.test(url)) return url;
  if (url.startsWith("//")) return `https:${url}`;
  if (url.startsWith("www.")) return `https://${url}`;

  return `https://${url.replace(/^\/+/, "")}`;
};

const isGenericSocialHomepage = (url) => {
  const normalizedUrl = String(url || "").toLowerCase().replace(/\/+$/, "");

  return [
    "https://www.facebook.com",
    "https://facebook.com",
    "https://www.instagram.com",
    "https://instagram.com",
    "https://www.linkedin.com",
    "https://linkedin.com",
    "https://www.twitter.com",
    "https://twitter.com",
    "https://www.x.com",
    "https://x.com",
  ].includes(normalizedUrl);
};

const normalizeSocialLinks = (links) => links.map((item, index) => {
  const name = typeof item === "string" ? "" : item?.name || item?.title || item?.platform || item?.type || "";
  const rawUrl = typeof item === "string" ? item : item?.redirect || item?.redirect_link || item?.url || item?.link || item?.href;
  const sourceText = normalizeText(`${name} ${rawUrl} ${item?.icon || ""}`);
  const meta = socialMeta.find((entry) => entry.keywords.some((keyword) => sourceText.includes(normalizeText(keyword)))) || socialMeta[socialMeta.length - 1];
  const url = normalizeSocialUrl(rawUrl);

  return url && !isGenericSocialHomepage(url) ? {
    id: item?.id || item?.social_link_id || `${meta.key}-${index}`,
    key: meta.key,
    label: name || meta.label,
    url,
    Icon: meta.icon,
  } : null;
}).filter(Boolean);

const RghtnowMarketingFooter = () => {
  const dispatch = useDispatch();
  const socialLinksData = useSelector((state) => state.common?.socialLinksData || []);
  const [apiSocialLinks, setApiSocialLinks] = useState([]);

  useEffect(() => {
    let mounted = true;

    dispatch(getSocialLinks())
      .then((response) => {
        if (mounted) setApiSocialLinks(getSocialLinksArray(response));
      })
      .catch(() => {
        if (mounted) setApiSocialLinks([]);
      });

    return () => {
      mounted = false;
    };
  }, [dispatch]);

  const socialLinks = normalizeSocialLinks(apiSocialLinks.length ? apiSocialLinks : socialLinksData);

  return (
    <footer className="rght-marketing-footer">
    <div className="container">
      <div className="rght-footer-top">
        <div>
          <h2>Start Selling on RGHT NOW Today!</h2>
          <p>More orders. More customers. More growth.</p>
        </div>
        <img className="rght-footer-rider" src={upload("2023/09/Group-304.svg")} alt="" />
        <div className="rght-footer-apps">
          <h3>Download App</h3>
          <a href="https://play.google.com/store/apps/details?id=com.rghtnow.user" target="_blank" rel="noreferrer" aria-label="Download RGHT NOW on Google Play">
            <img src={upload("2023/09/Google_Play_Store_badge_EN.svg-300x89.png")} alt="Get it on Google Play" />
          </a>
          <a href="https://apps.apple.com/us/app/rght-now/id6446073858" target="_blank" rel="noreferrer" aria-label="Download RGHT NOW on the App Store">
            <img src={upload("2023/09/Download_on_the_App_Store_Badge.svg-300x89.webp")} alt="Download on the App Store" />
          </a>
        </div>
      </div>

      <div className="rght-footer-main">
        <Link to="/home" className="rght-footer-logo" aria-label="RGHT NOW home">
          <img src={upload("2025/11/rghtnow-footer-logo.png")} alt="RGHT NOW" />
        </Link>
        <nav className="rght-footer-nav" aria-label="Footer navigation">
          {footerLinks.map((item) => (
            <Link key={item.to} to={item.to}>
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="rght-footer-social">
          <h3>Follow Us On</h3>
          <div>
            {socialLinks.map(({ id, key, label, url, Icon }) => (
              <a key={id} href={url} target="_blank" rel="noreferrer" aria-label={label} title={label} className={`rght-footer-social-${key}`}>
                <Icon weight="fill" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="rght-footer-bottom">
        <div>
          <Link to="/terms-and-conditions">Term and Conditions</Link>
          <Link to="/privacy-policy">Privacy Policy</Link>
        </div>
        <p>© 2025 Rght Now, All Rights Reserved.</p>
      </div>
    </div>
  </footer>
  );
};

export default RghtnowMarketingFooter;
